/**
 * Edit Profile Validation Schema
 * Zod schema for validating edit profile form data
 */

import { z } from "zod"

// Custom website validator - accepts URLs with or without protocol
const websiteSchema = z
  .string()
  .transform(val => val.trim())
  .transform(val => {
    // If doesn't start with http/https, add it
    if (val && !/^https?:\/\//i.test(val)) {
      return `https://${val}`
    }
    return val
  })
  .refine(
    val => {
      if (!val || val === "") return true
      try {
        const url = new URL(val)
        return url.protocol === "http:" || url.protocol === "https:"
      } catch {
        return false
      }
    },
    { message: "Please enter a valid website URL" },
  )
  .optional()
  .or(z.literal(""))

// Custom social media URL validator - validates domain or accepts username-only for Instagram
const createSocialUrlSchema = (domains: string[]) =>
  z
    .string()
    .transform(val => val?.trim())
    .transform(val => {
      if (!val) return val

      // For Instagram, allow username-only (no dots)
      if (domains.includes("instagram.com") && !val.includes(".") && !val.includes("/")) {
        return `https://instagram.com/${val}`
      }

      // Add protocol if missing
      if (val && !/^https?:\/\//i.test(val)) {
        return `https://${val}`
      }
      return val
    })
    .refine(
      val => {
        if (!val || val === "") return true
        try {
          const url = new URL(val)
          return domains.some(domain => url.hostname.includes(domain))
        } catch {
          return false
        }
      },
      {
        message: domains.includes("instagram.com")
          ? "Please enter a valid username or URL (e.g., username or instagram.com/username)"
          : `Please enter a valid URL for ${domains[0]}`,
      },
    )
    .optional()
    .or(z.literal(""))

export const editProfileSchema = z.object({
  name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters"),
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must not exceed 30 characters")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
  city: z.string().optional(),
  // Location fields from Google Places (all optional)
  streetAddress: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  zipCode: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  bio: z.string().max(500, "Description must not exceed 500 characters").optional(),
  credentials: z.string().optional(),
  tags: z.array(z.string()).max(10, "You can add up to 10 tags").optional(),
  // Social media URLs with custom validators
  website: websiteSchema,
  twitterUrl: createSocialUrlSchema(["twitter.com", "x.com"]),
  instagramUrl: createSocialUrlSchema(["instagram.com"]),
  facebookUrl: createSocialUrlSchema(["facebook.com", "fb.com"]),
})

export type EditProfileFormData = z.infer<typeof editProfileSchema>
